import React, { useCallback, useEffect, useRef } from "react";
import Footer from "../../components/ui/Footer";
import Navbar from "../../components/ui/Navbar";
import styles from "./Renew.module.css";
import { useDispatch, useSelector } from "react-redux";
import FixedAgent from "../../utils/api/FixedAgent";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router";
import { userDataActions } from "../../store/redux-store/Index";
import Auth from "../../utils/api/Auth";
import { parseMonthsFromExpiration } from "../../utils/parseMonthsFromExpiration";
import Papa from "papaparse";
import { CEI_MAKES, NICON_MAKES } from "../../utils/constants/vehicleData";
import { showSuccessToast, showErrorToast } from "../../utils/toastFactory";
import { useMakeModelLookup } from "../../hooks/useMakeModelLookup";
import { useVehicleData } from "../../hooks/useVehicleData";
import { useFormData } from "../../hooks/useFormData";
import { useAutofillTracking } from "../../hooks/useAutofillTracking";

// Sub-components
import WarningModal from "../../components/renewal/WarningModal";
import DetailsModal from "../../components/renewal/DetailsModal";
import VehicleFormFields from "../../components/renewal/VehicleFormFields";

function RenewInfo() {
  const isSubmittingRef = useRef(false);
  const hasAutoLookedUpRef = useRef(false);

  // --- Redux state ---
  const user = useSelector((state) => state.userData.personalInfo);
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userData.loginData.token);
  const renewData = useSelector((state) => state.userData.renewPapersData);
  const makeModelMapping = useSelector(
    (state) => state.userData.makeModelMapping,
  );
  console.log("makeModelMapping", makeModelMapping);
  const navigate = useNavigate();

  // --- Constants ---
  const ceiMake = CEI_MAKES;
  const niconMake = NICON_MAKES;

  // --- Custom Hooks ---
  const { input, setInput } = useFormData();

  const {
    lookupMakeId,
    lookupModelId,
    makeIdLoading,
    makeIdStatus,
    modelIdLoading,
    modelIdStatus,
  } = useMakeModelLookup(
    makeModelMapping,
    renewData,
    ceiMake,
    niconMake,
    token,
  );

  const { vehicleColor, vehicleModel, yearRange } = useVehicleData(
    input.make,
    makeModelMapping,
    renewData,
    ceiMake,
    niconMake,
    token,
  );

  // --- Local state for UI ---
  const [isLoading, setIsLoading] = React.useState(false);
  const [commercialType, setCommercialType] = React.useState("");
  const [modalOpen, setModalOpen] = React.useState(false);
  const [darakCheckResult, setDarakCheckResult] = React.useState(null);
  const [showWarningModal, setShowWarningModal] = React.useState(false);

  // --- Autofill Performance Tracking ---
  useAutofillTracking(input, hasAutoLookedUpRef.current);

  // --- Fetch Make/Model mapping from Google Sheets ---
  useEffect(() => {
    const fetchMapping = async () => {
      // Re-fetch if data is stale (older than 1 hour) or not yet loaded
      const STALE_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour
      const isStale =
        makeModelMapping.lastFetched &&
        Date.now() - new Date(makeModelMapping.lastFetched).getTime() >
          STALE_THRESHOLD_MS;

      if (makeModelMapping.loading) return;
      if (makeModelMapping.data.length > 0 && !isStale) return;

      if (isStale) {
        console.log("[CSV Fetch] Data is stale, re-fetching...", {
          lastFetched: makeModelMapping.lastFetched,
          ageMinutes: Math.round(
            (Date.now() - new Date(makeModelMapping.lastFetched).getTime()) /
              60000,
          ),
        });
      }

      const sheetUrl =
        process.env.REACT_APP_MAKE_MODEL_SHEET_URL ||
        "https://docs.google.com/spreadsheets/d/e/2PACX-1vS1DFB_KZAT74UewtAT7GUG-gQ2LLx4ELHifzknndGhQgza7Gl55EKfARR-JDK1k3HFJ0SQmzJTWa07/pub?output=csv";

      if (!sheetUrl) return;

      try {
        dispatch(userDataActions.setMakeModelMappingLoading(true));

        const response = await FixedAgent.fetchMakeModelSpreadsheet({
          sheetUrl,
        });

        const parsedData = Papa.parse(response.data, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (header) => header.trim(),
        });

        if (parsedData.errors.length > 0) {
          throw new Error("Failed to parse spreadsheet data");
        }

        dispatch(userDataActions.setMakeModelMappingData(parsedData.data));
      } catch (error) {
        dispatch(
          userDataActions.setMakeModelMappingError(
            error?.message || "Failed to load Make/Model data",
          ),
        );
        showErrorToast(
          "Unable to load Make/Model data. Will proceed to fallback data.",
        );
      }
    };

    fetchMapping();
  }, [
    dispatch,
    makeModelMapping.data.length,
    makeModelMapping.loading,
    makeModelMapping.lastFetched,
  ]);

  // --- Auto-lookup Make/Model from store data ---
  useEffect(() => {
    const autoLookupFromStoreData = async () => {
      if (makeModelMapping.data.length === 0) return;
      if (!renewData?.getNiconInsuranceRes) return;
      if (hasAutoLookedUpRef.current) return;

      const makeName =
        renewData.getNiconInsuranceRes.vehicle_make_custom?.trim() ||
        renewData.getNiconInsuranceRes.make?.trim() ||
        renewData.getNiconInsuranceRes.vehicleMake?.trim();

      const modelName =
        renewData.getNiconInsuranceRes.model?.trim() ||
        renewData.getNiconInsuranceRes.vehicleModel?.trim();

      if (!makeName) return;

      try {
        hasAutoLookedUpRef.current = true;

        if (makeName) {
          const makeId = await lookupMakeId(makeName);
          if (makeId) {
            setInput((prev) => ({ ...prev, make: makeId }));
          }
        }

        if (modelName && makeName) {
          const modelId = await lookupModelId(modelName, makeName);
          if (modelId) {
            setInput((prev) => ({ ...prev, model: modelId }));
          }
        }
      } catch (error) {
        hasAutoLookedUpRef.current = false;
      }
    };

    autoLookupFromStoreData();
  }, [
    makeModelMapping.data.length,
    renewData?.getNiconInsuranceRes,
    lookupMakeId,
    lookupModelId,
    setInput,
  ]);

  // --- Handle input changes with Make/Model ID enrichment ---
  const handleInputChange = useCallback(
    async (e) => {
      const { name, value } = e.target;
      setInput((prev) => ({ ...prev, [name]: value }));

      // Trigger Make ID lookup when Make is selected
      if (name === "make" && value) {
        const makeArray =
          renewData.insuranceType === "NICON" ? niconMake : ceiMake;
        const selectedMake = makeArray.find((make) => make.id === value);

        if (selectedMake) {
          let finalMakeId = selectedMake.id;

          const spreadSheetId = await lookupMakeId(selectedMake.name);
          if (spreadSheetId) {
            finalMakeId = spreadSheetId;
          }

          setInput((prev) => ({ ...prev, [name]: finalMakeId }));
          return;
        }
      }

      // Trigger Model ID lookup when Model is selected
      if (name === "model" && value) {
        const selectedModel = vehicleModel.find((model) =>
          renewData.insuranceType === "NICON"
            ? model.id === value
            : model.vehicleModelId === value,
        );

        const makeArray =
          renewData.insuranceType === "NICON" ? niconMake : ceiMake;
        const selectedMake = makeArray.find((make) => make.id === input.make);

        if (selectedModel && selectedMake) {
          let finalModelId = selectedModel.id;

          const spreadSheetId = await lookupModelId(
            selectedModel.name,
            selectedMake.name,
          );
          if (spreadSheetId) {
            finalModelId = spreadSheetId;
          }

          setInput((prev) => ({ ...prev, [name]: finalModelId }));
          return;
        }
      }
    },
    [
      setInput,
      renewData.insuranceType,
      niconMake,
      ceiMake,
      vehicleModel,
      input.make,
      lookupMakeId,
      lookupModelId,
    ],
  );

  // --- Prefill form from renewData ---
  useEffect(() => {
    if (renewData) {
      setInput((prev) => ({
        ...prev,
        fullname:
          renewData?.getNiconInsuranceRes?.insured_name ||
          renewData?.getNiconInsuranceRes?.full_name ||
          renewData?.getNiconInsuranceRes?.insured,
        phone:
          renewData?.getNiconInsuranceRes?.phone ||
          renewData?.getNiconInsuranceRes?.phone_no ||
          user?.phone_no ||
          renewData?.getNiconInsuranceRes?.phoneNumber,
        chasisNo:
          renewData?.getNiconInsuranceRes?.chasis_no ||
          renewData?.getNiconInsuranceRes?.chassis_no ||
          renewData?.getNiconInsuranceRes?.chassis,
        engineNo:
          renewData?.getNiconInsuranceRes?.engine_no ||
          renewData?.getNiconInsuranceRes?.engineNo,
        email: renewData?.getNiconInsuranceRes?.email || user?.email,
        plate_no: renewData?.plateNumber,
      }));
    }
  }, [renewData, user?.email, user?.phone_no, setInput]);

  // --- Derive color/year from renewData if not yet set ---
  const getColorValue = useCallback(
    (currentInput) => {
      if (currentInput?.color) return currentInput.color;
      const rawColor = renewData?.getNiconInsuranceRes?.color;
      const trimmedColor = rawColor?.trim().toLowerCase();
      if (trimmedColor) {
        const foundColor = vehicleColor?.find(
          (color) => color?.colorName?.toLowerCase()?.trim() === trimmedColor,
        );
        return foundColor?.colorName || foundColor?.vehicleColorId;
      } else {
        const alternateColor = renewData?.vehicleColor?.toLowerCase()?.trim();
        const foundColor = vehicleColor?.find(
          (color) =>
            color?.colorName?.toLowerCase()?.trim() ===
            (trimmedColor || alternateColor),
        );
        return foundColor?.colorName || foundColor?.vehicleColorId;
      }
    },
    [renewData, vehicleColor],
  );

  const getYearValue = useCallback(
    (currentInput) => {
      return (
        currentInput.year ||
        renewData?.getNiconInsuranceRes?.year_of_make ||
        renewData?.getNiconInsuranceRes?.year ||
        ""
      );
    },
    [renewData],
  );

  useEffect(() => {
    const newColor = getColorValue(input);
    const newYear = getYearValue(input);

    if ((!input.color && newColor) || (!input.year && newYear)) {
      setInput((prev) => ({
        ...prev,
        color: prev.color || newColor,
        year: prev.year || newYear,
      }));
    }
    // eslint-disable-next-line
  }, [renewData, vehicleColor, vehicleModel]);

  // --- Fetch second insurance data (fallback for customer add) ---
  const fetchUserSecondInsuranceData = useCallback(
    async (plate_no) => {
      try {
        const getUserSecondInsuranceRes =
          await FixedAgent.getCustomerInsuranceOnlineData({
            plate_no,
            token,
          });

        const getUserRoadRes = await FixedAgent.getRoadData({
          plate_no: plate_no,
          token,
        });

        const addNewUser = await FixedAgent.addNewCustomer({
          phone_no: input.phone,
          plate_no: renewData?.plateNumber,
          fullname: getUserSecondInsuranceRes.data?.data?.full_name,
          token: token,
          engine_number: getUserSecondInsuranceRes.data?.data?.engine_no,
          chassis: getUserSecondInsuranceRes.data?.data?.chassis_no,
          make: getUserSecondInsuranceRes.data?.data?.make,
          model: getUserSecondInsuranceRes.data?.data?.model,
          year: getUserSecondInsuranceRes.data?.data?.year,
          road_expiry_date: getUserRoadRes.data.data.RwcExp,
          road_message: getUserRoadRes.data.data.message,
          color: getUserSecondInsuranceRes.data?.data?.color,
          state: "lagos",
        });

        setIsLoading(false);
        return addNewUser;
      } catch (err) {
        setIsLoading(false);

        if (err?.response?.data?.message === "Unauthenticated") {
          dispatch(userDataActions.logout());
          window.location.reload();
        }
        if (
          err?.response?.data?.message ===
          "Policy details are invalid or not matching."
        ) {
          dispatch(userDataActions.setUserPlateNumber(input.plate_no));
          setIsLoading(true);
          navigate("/add-customer");
        } else {
          showErrorToast(err?.response?.data?.message);
        }
      }
    },
    [
      token,
      dispatch,
      input.phone,
      renewData?.plateNumber,
      navigate,
      input.plate_no,
    ],
  );

  // --- Add customer handler ---
  const addCustomerHandler = useCallback(async () => {
    try {
      setIsLoading(true);

      let getInsuranceRes = { data: { message: null, expiryDate: null } };
      try {
        const response = await FixedAgent.getInsuranceData({
          plate_no: input.plate_no,
          token,
        });

        if (response.status === 200) {
          getInsuranceRes = response;
        }
      } catch (error) {
        // Insurance data fetch failed, use defaults
      }

      const addNewUser = await FixedAgent.addNewCustomer({
        phone_no: input.phone,
        plate_no: input.plate_no,
        fullname:
          renewData?.getNiconInsuranceRes?.insured_name ||
          renewData?.getNiconInsuranceRes?.full_name,
        token: token,
        engine_number: renewData?.getNiconInsuranceRes?.engine_no,
        chassis:
          renewData?.getNiconInsuranceRes?.chasis_no ||
          renewData?.getNiconInsuranceRes?.chassis_no,
        make:
          renewData?.getNiconInsuranceRes?.vehicle_make_custom ||
          renewData?.getNiconInsuranceRes?.make,
        model:
          renewData?.getNiconInsuranceRes?.vehicle_model_custom ||
          renewData?.getNiconInsuranceRes?.model,
        year:
          renewData?.getNiconInsuranceRes?.year_of_make ||
          renewData?.getNiconInsuranceRes?.year,
        expiry_date: getInsuranceRes?.data?.message,
        color:
          renewData?.getNiconInsuranceRes?.color ||
          renewData?.getNiconInsuranceRes?.color,
        state: "lagos",
      });

      submitRenewHandler();

      setIsLoading(false);
      return addNewUser;
    } catch (err) {
      setIsLoading(false);

      if (
        err?.response?.data?.message ===
        "Policy details are invalid or not matching."
      ) {
        setIsLoading(true);
        fetchUserSecondInsuranceData(input.plate_no);
      } else {
        showErrorToast(err?.response?.data?.message);
      }

      if (err?.response?.data?.message === "Unauthenticated") {
        dispatch(userDataActions.logout());
        window.location.reload();
      }
    }
    // eslint-disable-next-line
  }, [input, token, renewData, dispatch, fetchUserSecondInsuranceData]);

  // --- Darak check handler ---
  const handleDarakCheck = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const darakResponse = await FixedAgent.checkDaraKStatus({
        plate_no: input.plate_no,
        token,
      });

      const darakData = darakResponse.data.data;
      setDarakCheckResult(darakData);

      if (darakData.is_darak) {
        const monthsRemaining = parseMonthsFromExpiration(
          darakData.policy_expiration_status,
        );

        if (monthsRemaining >= 10) {
          setShowWarningModal(true);
          setIsLoading(false);
          return;
        }
      }

      setModalOpen(true);
      setIsLoading(false);
    } catch (error) {
      setModalOpen(true);
      setIsLoading(false);
    }
  };

  // --- Submit renewal handler ---
  const submitRenewHandler = async (e) => {
    e?.preventDefault();

    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsLoading(true);
    try {
      let makeOrderRes;
      await Auth.updateUserHandler({
        plateNumber: input.plate_no,
        fullname: input.fullname,
        email: input.email,
        phone_no: input.phone,
        date_of_birth: input.dob,
        gender: input.gender,
        occupation: input.occupation,
        address: input.address,
        chassis: input.chasisNo,
        engine_number: input.engineNo,
        make:
          ceiMake.find((make) => make.id === input.make)?.name ||
          niconMake.find((make) => make.id === input.make)?.name ||
          input.make,
        model:
          renewData.insuranceType !== "NICON"
            ? vehicleModel.find(
                (model) => model.vehicleModelId === Number(input.model),
              )?.modelName
            : vehicleModel.find((model) => model.id === input.model)?.name,
        year: input.year,
        token: token,
      });

      if (renewData.insuranceType === "NICON") {
        makeOrderRes = await FixedAgent.makeNiconManualInsuranceOrder({
          product_id: renewData?.productsDetails?.id,
          plate_no: input.plate_no,
          payment_option: renewData?.paymentOption,
          fullname: input.fullname,
          email: input.email,
          chassis: input.chasisNo,
          engine_number: input.engineNo,
          nicon_make_id: input.make,
          nicon_model_id: input.model,
          year: input.year,
          color: input.color,
          EffectiveDate: input.effectiveDate,
          token,
        });
      } else {
        makeOrderRes = await FixedAgent.makeCEIManualInsuranceOrder({
          product_id: renewData?.productsDetails?.id,
          plate_no: input.plate_no,
          payment_option: renewData?.paymentOption,
          fullname: input.fullname,
          email: input.email,
          chassis: input.chasisNo,
          engine_number: input.engineNo,
          cei_make_id: input.make,
          cei_model_id: input.model,
          year: input.year,
          cei_color_id: input.color,
          commercial_type: commercialType,
          token,
        });
      }

      showSuccessToast("Successful");
      navigate(`/history/${makeOrderRes.data.id}`);

      setIsLoading(false);
      isSubmittingRef.current = false;
      return makeOrderRes;
    } catch (err) {
      if (
        err?.response?.data?.message === "Plate not found!" ||
        err?.response?.data?.message === "Customer not found!!!"
      ) {
        setIsLoading(true);
        isSubmittingRef.current = false;
        addCustomerHandler();
        return;
      }

      setIsLoading(false);
      isSubmittingRef.current = false;

      if (
        err?.response?.data.error === "Request failed" ||
        err?.response?.data?.message === "Request failed"
      ) {
        // Send WhatsApp alert
        if (renewData.insuranceType === "NICON") {
          await FixedAgent.sendWhatsAppMessageToNonUser({
            phone_no: "09152647290",
            message: `This is from Nicon -- Policy type - ${
              renewData.productsDetails.pin_type
            }. -- Agent: ${user.fullname} - ${JSON.stringify(
              err.response.data,
            )}`,
            token: token,
          });
        } else {
          await FixedAgent.sendWhatsAppMessageToNonUser({
            phone_no: "09152647290",
            message: `This is from CEI -- Policy type - ${renewData.productsDetails.pin_type}. -- Agent: ${user.fullname} ${err.response.data.message}`,
            token: token,
          });
        }

        // Handle specific error messages
        const errorMsg =
          renewData.insuranceType === "NICON"
            ? err.response.data.error
            : err.response.data.message;

        if (errorMsg?.includes("Policy already created")) {
          showErrorToast("Policy already created");
        } else if (
          errorMsg?.includes("Insufficient credit. Contact your administrator")
        ) {
          showErrorToast("Insufficient credit. Contact your administrator");
        } else if (
          errorMsg?.includes(
            "Registration number already exist. Please ensure this is not a case of duplicated",
          )
        ) {
          showErrorToast(
            "Registration number already exists. Please ensure this is not a case of duplicated transaction.",
          );
        } else if (errorMsg?.includes("Wallet is exhausted.")) {
          showErrorToast("Wallet is exhausted.");
        } else if (
          errorMsg?.includes("One or more validation errors occurred.")
        ) {
          showErrorToast(
            "One or more validation errors occurred, Contact the management for support.",
          );
        } else {
          showErrorToast("System Error, Contact the management for support.");
        }
      } else {
        showErrorToast(
          err?.response?.data?.error || err?.response?.data?.message,
        );
      }
    }
  };

  // --- Certificate URL ---
  const certUrl = `https://api.prod.myagentpro.ng/api/v1/app/fixedagent/customer/order/nicon/cert/${(
    input.plate_no ||
    renewData?.plateNumber ||
    ""
  ).toUpperCase()}`;

  // --- Render ---
  return (
    <div>
      <ToastContainer />

      <WarningModal
        isOpen={showWarningModal && !!darakCheckResult}
        onClose={() => setShowWarningModal(false)}
        certUrl={certUrl}
      />

      <DetailsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={submitRenewHandler}
        input={input}
        renewData={renewData}
        vehicleModel={vehicleModel}
        vehicleColor={vehicleColor}
        ceiMake={ceiMake}
        niconMake={niconMake}
        isLoading={isLoading}
        isSubmitting={isSubmittingRef.current}
      />

      <Navbar title="Renew" />
      <div className={styles.body}>
        <form onSubmit={handleDarakCheck} className={styles.body_inner}>
          {/* Personal Details Section */}
          <div className={styles.wrapper}>
            <h1 className={styles.wrapper_title}>Confirm Your Details</h1>
            <div className={styles.input_container}>
              <input
                className={styles.input_field}
                type="text"
                name="fullname"
                required
                value={input.fullname}
                onChange={handleInputChange}
                placeholder="Full Name"
              />

              <input
                className={styles.input_field}
                type="text"
                required
                name="phone"
                onChange={handleInputChange}
                value={input.phone}
                placeholder="Phone Number"
              />
            </div>
          </div>

          {/* VIN/Chasis Section */}
          <div className={styles.wrapper}>
            <h1 className={styles.wrapper_title}>Enter Plate VIN to Begin!</h1>
            <div className={styles.input_container}>
              <input
                className={styles.input_field}
                type="text"
                required
                name="chasisNo"
                onChange={handleInputChange}
                value={input.chasisNo}
                placeholder="VIN/Chasis"
              />
            </div>
          </div>

          {/* Effective Date (NICON only) */}
          {renewData.insuranceType === "NICON" && (
            <div className={styles.wrapper}>
              <h1 className={styles.wrapper_title}>Enter Effective Date</h1>
              <div className={styles.input_container}>
                <input
                  className={styles.input_field}
                  type="date"
                  required
                  name="effectiveDate"
                  onChange={handleInputChange}
                  value={input.effectiveDate}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>
            </div>
          )}

          {/* Vehicle Form Fields */}
          <VehicleFormFields
            input={input}
            onInputChange={handleInputChange}
            renewData={renewData}
            vehicleModel={vehicleModel}
            vehicleColor={vehicleColor}
            ceiMake={ceiMake}
            niconMake={niconMake}
            yearRange={yearRange}
            makeIdLoading={makeIdLoading}
            makeIdStatus={makeIdStatus}
            modelIdLoading={modelIdLoading}
            modelIdStatus={modelIdStatus}
            commercialType={commercialType}
            onCommercialTypeChange={(e) => setCommercialType(e.target.value)}
          />

          {/* Submit Button */}
          <button
            disabled={isLoading || isSubmittingRef.current}
            style={{
              cursor:
                isLoading || isSubmittingRef.current
                  ? "not-allowed"
                  : "pointer",
              opacity: isLoading || isSubmittingRef.current ? 0.5 : 1,
            }}
            className={styles.submit_button}
          >
            {isLoading ? "Loading..." : "Renew"}
          </button>
        </form>
      </div>
      <Footer />
    </div>
  );
}

export default RenewInfo;
